import type {QueryClient} from '@tanstack/react-query'

import type {MemexItemModel} from '../../../../models/memex-item-model'
import {useSidePanelItemQuery} from '../../../../queries/side-panel'
import {
  buildMemexGroupedItemsQueryKey,
  buildMemexUngroupedItemsQueryKey,
  type MemexSubIssuesQueryKey,
  type PageTypeForGroupedItems,
} from '../../queries/query-keys'
import type {
  MemexItemsPageQueryData,
  MemexSubIssuesQueryData,
  PageParam,
  PaginatedMemexItemsQueryVariables,
} from '../../queries/types'
import {findMemexItemByIdInQueryClient} from './read-items'

/**
 * A thin wrapper around getQueryData for getting the memex items data for a single page/query.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're getting to be included in the query key
 */
export function getQueryDataForMemexItemsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageParam: PageParam,
): MemexItemsPageQueryData | undefined {
  const queryKey = buildMemexUngroupedItemsQueryKey(variables, pageParam)
  return queryClient.getQueryData<MemexItemsPageQueryData>(queryKey)
}

/**
 * A thin wrapper around getQueryData for getting the memex items data for a single page/query of grouped items.
 * @param queryClient
 * @param variables PaginatedMemexItemsQueryVariables used in the query key
 * @param pageParam Page param for the data we're getting to be included in the query key
 * @param pageType Page type with a group id of the data we're getting to be included in the query key
 */
export function getQueryDataForGroupedItemsPage(
  queryClient: QueryClient,
  variables: PaginatedMemexItemsQueryVariables,
  pageType: PageTypeForGroupedItems,
  pageParam: PageParam,
): MemexItemsPageQueryData | undefined {
  const queryKey = buildMemexGroupedItemsQueryKey(variables, pageType, pageParam)
  return queryClient.getQueryData<MemexItemsPageQueryData>(queryKey)
}

/**
 * A thin wrapper around getQueryData for getting sub-issues data for a pre-built query key.
 * @param queryClient
 * @param queryKey The already-built query key for the sub-issues query
 */
export function getQueryDataForSubIssuesPage(
  queryClient: QueryClient,
  queryKey: MemexSubIssuesQueryKey,
): MemexSubIssuesQueryData | undefined {
  return queryClient.getQueryData<MemexSubIssuesQueryData>(queryKey)
}

/**
 * Finds data for a given side panel itemId in the query cache
 * Returns undefined if no data is found
 */
export function getQueryDataForSidePanelItemFromQueryClient(queryClient: QueryClient, itemId: string | null) {
  if (!itemId || !parseInt(itemId)) return undefined
  return queryClient.getQueryData<MemexItemModel | undefined>(useSidePanelItemQuery.getKey({itemId}))
}

/**
 * Finds initial data for a given side panel itemId in the query cache
 * - First checks the side panel query cache
 * - Then checks the main items query cache
 * Returns undefined if no data is found
 */
export function getInitialDataForSidePanelItem(queryClient: QueryClient, itemId: string | number | null) {
  if (!itemId) return undefined
  const parsedItemId = Number(itemId)
  const existingData = queryClient.getQueryData<MemexItemModel | undefined>(
    useSidePanelItemQuery.getKey({itemId: parsedItemId.toString()}),
  )
  if (existingData) return existingData
  return findMemexItemByIdInQueryClient(queryClient, parsedItemId)
}
